import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, Sparkle, Bank, Warning, Calendar, Target, Upload, ChartLineUp, Receipt, TrendUp, Calculator, Vault } from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const COLORS = ["#D90429", "#2A9D8F", "#2B2D42", "#E9C46A", "#6C757D", "#6f42c1"];
const FMT = (v) => (v || 0).toLocaleString("fr-CA", { style: "currency", currency: "CAD" });
const TABS = [
  { k: "overview", label: "Vue d'ensemble", icon: ChartLineUp },
  { k: "debts", label: "Dettes", icon: Warning },
  { k: "bills", label: "Factures", icon: Receipt },
  { k: "goals", label: "Objectifs", icon: Target },
  { k: "transactions", label: "Transactions", icon: TrendUp },
  { k: "taxes", label: "TPS / TVQ", icon: Calculator },
  { k: "import", label: "Import CSV", icon: Upload },
  { k: "strategy", label: "Stratégie IA", icon: Sparkle },
];

export default function FinanceModule() {
  const [tab, setTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [txs, setTxs] = useState([]);
  const [debts, setDebts] = useState([]);
  const [bills, setBills] = useState([]);
  const [goals, setGoals] = useState([]);

  const reload = async () => {
    const [d, t, de, b, g] = await Promise.all([
      api.get("/finance/dashboard").then(r => r.data),
      api.get("/finance/transactions").then(r => r.data),
      api.get("/finance/debts").then(r => r.data),
      api.get("/finance/bills").then(r => r.data),
      api.get("/finance/goals").then(r => r.data),
    ]);
    setDashboard(d); setTxs(t); setDebts(de); setBills(b); setGoals(g);
  };
  useEffect(() => { reload().catch(() => {}); }, []);

  return (
    <div data-testid="finance-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader
          title="Finances"
          icon={ChartLineUp}
          breadcrumbs={[{ label: "Finance & administration" }, { label: "Finances" }]}
          subtitle="Dettes, factures, objectifs et stratégies IA — perso, compagnie et fondation."
        />
      </div>

      {/* Alerts bar */}
      {dashboard?.alerts?.length > 0 && (
        <div className="hard-border-b bg-[#FFF4F4]">
          <div className="px-8 lg:px-12 py-4 flex flex-wrap gap-3">
            {dashboard.alerts.map((a, i) => (
              <div key={i} className="flex items-center gap-2 hard-border px-3 py-1.5 bg-white" data-testid={`alert-${i}`}>
                <Warning size={14} weight="fill" className={a.level === "rouge" ? "text-[#D90429]" : "text-[#E9C46A]"}/>
                <span className="text-sm font-semibold">{a.title}</span>
                <span className="ticker text-muted-foreground">{a.detail}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="hard-border-b px-8 lg:px-12 overflow-x-auto">
        <div className="flex">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.k} data-testid={`fin-tab-${t.k}`} onClick={() => setTab(t.k)}
                className={`flex items-center gap-2 px-4 py-4 ticker border-b-2 transition-colors whitespace-nowrap ${tab === t.k ? "border-[#D90429] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                <Icon size={14}/> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-8 lg:px-12 py-8">
        {tab === "overview" && <Overview dashboard={dashboard}/>}
        {tab === "debts" && <DebtsPanel debts={debts} reload={reload}/>}
        {tab === "bills" && <BillsPanel bills={bills} reload={reload}/>}
        {tab === "goals" && <GoalsPanel goals={goals} reload={reload}/>}
        {tab === "transactions" && <TransactionsPanel txs={txs} reload={reload}/>}
        {tab === "taxes" && <TaxesPanel/>}
        {tab === "import" && <ImportPanel reload={reload}/>}
        {tab === "strategy" && <StrategyPanel/>}
      </div>
    </div>
  );
}

function Overview({ dashboard }) {
  if (!dashboard) return <div className="text-sm text-muted-foreground">Chargement…</div>;
  const s = dashboard.summary;
  const ceo = dashboard.ceo_metrics;

  const barData = ["perso", "compagnie", "fondation"].map(acc => ({
    name: acc[0].toUpperCase() + acc.slice(1),
    Revenus: s[acc].revenu,
    Dépenses: s[acc].depense,
  }));

  const downloadCSV = async (endpoint, filename) => {
    try {
      const token = localStorage.getItem("map_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/exports/${endpoint}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const text = await r.text();
      const blob = new Blob([text], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      toast.success(`${filename} téléchargé`);
    } catch { toast.error("Erreur export"); }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="hard-border p-5 bg-secondary/40 flex items-start gap-4 flex-1 min-w-[280px]">
          <Bank size={22} className="text-[#D90429] mt-0.5"/>
          <div>
            <div className="font-semibold text-sm">Liaison bancaire automatique — phase 2</div>
            <div className="text-sm text-muted-foreground mt-1">En attendant, entre tes transactions manuellement ou utilise l'import CSV.</div>
          </div>
        </div>
        <div className="flex gap-2" data-testid="export-buttons">
          <button onClick={() => downloadCSV("transactions.csv", "transactions.csv")} data-testid="export-tx" className="hard-border px-3 py-2 ticker hover:bg-secondary flex items-center gap-2 transition-colors">
            <Upload size={14} className="rotate-180"/> CSV transactions
          </button>
          <button onClick={() => downloadCSV("debts.csv", "dettes.csv")} data-testid="export-debts" className="hard-border px-3 py-2 ticker hover:bg-secondary flex items-center gap-2 transition-colors">
            <Upload size={14} className="rotate-180"/> CSV dettes
          </button>
          <button onClick={() => downloadCSV("tax-entries.csv", "tps_tvq.csv")} data-testid="export-tax" className="hard-border px-3 py-2 ticker hover:bg-secondary flex items-center gap-2 transition-colors">
            <Upload size={14} className="rotate-180"/> CSV TPS/TVQ
          </button>
        </div>
      </div>

      {/* 7 chiffres CEO */}
      <div>
        <div className="ticker text-muted-foreground mb-3">Les 7 chiffres du CEO (30 derniers jours)</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 hard-border">
          <KPI label="Revenus 30j" value={FMT(ceo.revenus_30j)} testId="ceo-rev"/>
          <KPI label="Dépenses 30j" value={FMT(ceo.depenses_30j)} bordered testId="ceo-dep"/>
          <KPI label="Profit 30j" value={FMT(ceo.profit_30j)} highlight={ceo.profit_30j < 0} bordered testId="ceo-profit"/>
          <KPI label="Dettes totales" value={FMT(ceo.dettes_totales)} highlight bordered testId="ceo-debt"/>
          <KPI label="Factures mensuelles" value={FMT(ceo.factures_mensuelles)} testId="ceo-bills"/>
          <KPI label="Intérêts / mois" value={FMT(ceo.interet_mensuel)} bordered highlight testId="ceo-int"/>
          <KPI label="Objectifs actifs" value={ceo.objectifs_actifs} bordered testId="ceo-goals"/>
          <KPI label="Paiements min. dette" value={FMT(dashboard.debts_min_payment)} bordered testId="ceo-min"/>
        </div>
      </div>

      {/* Accounts breakdown */}
      <div>
        <div className="ticker text-muted-foreground mb-3">Comptes</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 hard-border">
          {["perso", "compagnie", "fondation"].map((acc, i) => (
            <div key={acc} className={`p-6 ${i > 0 ? "md:border-l border-border" : ""}`}>
              <div className="ticker text-muted-foreground mb-2">{acc}</div>
              <div className="font-mono text-2xl font-semibold" data-testid={`acc-${acc}`}>{FMT(s[acc].solde)}</div>
              <div className="ticker text-muted-foreground mt-2">Rev. {FMT(s[acc].revenu)} · Dép. {FMT(s[acc].depense)}</div>
            </div>
          ))}
        </div>
      </div>

      {dashboard.summary.total_count > 0 && (
        <div className="hard-border p-6">
          <div className="ticker text-muted-foreground mb-4">Revenus vs dépenses par compte</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
              <XAxis dataKey="name" stroke="#6C757D" fontSize={11}/>
              <YAxis stroke="#6C757D" fontSize={11}/>
              <Tooltip/>
              <Legend wrapperStyle={{ fontSize: 11 }}/>
              <Bar dataKey="Revenus" fill="#2A9D8F"/>
              <Bar dataKey="Dépenses" fill="#D90429"/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function DebtsPanel({ debts, reload }) {
  const empty = { creditor: "", account: "perso", balance: "", interest_rate: "", min_payment: "", due_day: "", priority: "orange", status: "actif", notes: "" };
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/debts", {
        ...form,
        balance: parseFloat(form.balance),
        interest_rate: parseFloat(form.interest_rate) || 0,
        min_payment: parseFloat(form.min_payment) || 0,
        due_day: form.due_day ? parseInt(form.due_day) : null,
      });
      setForm(empty); setOpen(false); reload();
      toast.success("Dette enregistrée");
    } catch { toast.error("Erreur"); }
  };

  const del = async (id) => { await api.delete(`/finance/debts/${id}`); reload(); };

  const total = debts.reduce((s, d) => s + d.balance, 0);
  const pie = debts.map((d, i) => ({ name: d.creditor, value: d.balance, fill: COLORS[i % COLORS.length] }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display font-bold text-2xl">{FMT(total)}</div>
          <div className="ticker text-muted-foreground">Dettes totales · {debts.length} créanciers</div>
        </div>
        <button data-testid="debt-add-btn" onClick={() => setOpen(!open)} className="bg-[#D90429] hover:bg-[#BF0424] text-white px-5 py-2.5 ticker flex items-center gap-2 transition-colors">
          <Plus size={14}/> {open ? "Annuler" : "Ajouter"}
        </button>
      </div>

      {open && (
        <form onSubmit={add} className="hard-border p-5 grid grid-cols-1 md:grid-cols-4 gap-3 bg-secondary/30" data-testid="debt-form">
          <input data-testid="d-creditor" required placeholder="Créancier (ex: Visa Infinite)" value={form.creditor} onChange={(e)=>setForm({...form, creditor:e.target.value})} className="hard-border px-3 py-2 md:col-span-2"/>
          <select data-testid="d-account" value={form.account} onChange={(e)=>setForm({...form, account:e.target.value})} className="hard-border px-3 py-2 bg-white">
            <option value="perso">Perso</option><option value="compagnie">Compagnie</option><option value="fondation">Fondation</option>
          </select>
          <select data-testid="d-priority" value={form.priority} onChange={(e)=>setForm({...form, priority:e.target.value})} className="hard-border px-3 py-2 bg-white">
            <option value="rouge">🔴 Rouge</option><option value="orange">🟠 Orange</option><option value="vert">🟢 Vert</option>
          </select>
          <input data-testid="d-balance" required type="number" step="0.01" placeholder="Solde $" value={form.balance} onChange={(e)=>setForm({...form, balance:e.target.value})} className="hard-border px-3 py-2 font-mono"/>
          <input data-testid="d-rate" type="number" step="0.01" placeholder="Taux % annuel" value={form.interest_rate} onChange={(e)=>setForm({...form, interest_rate:e.target.value})} className="hard-border px-3 py-2 font-mono"/>
          <input data-testid="d-min" type="number" step="0.01" placeholder="Paiement min $" value={form.min_payment} onChange={(e)=>setForm({...form, min_payment:e.target.value})} className="hard-border px-3 py-2 font-mono"/>
          <input data-testid="d-due" type="number" min="1" max="31" placeholder="Jour dû (1-31)" value={form.due_day} onChange={(e)=>setForm({...form, due_day:e.target.value})} className="hard-border px-3 py-2"/>
          <select data-testid="d-status" value={form.status} onChange={(e)=>setForm({...form, status:e.target.value})} className="hard-border px-3 py-2 bg-white md:col-span-2">
            <option value="actif">Actif</option><option value="en_retard">En retard</option><option value="entente">Entente en cours</option><option value="regle">Réglée</option>
          </select>
          <input data-testid="d-notes" placeholder="Notes" value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} className="hard-border px-3 py-2 md:col-span-2"/>
          <button data-testid="d-submit" className="bg-foreground hover:bg-[#D90429] text-white py-2.5 ticker md:col-span-4 transition-colors">Enregistrer la dette</button>
        </form>
      )}

      {debts.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="hard-border p-5 lg:col-span-1">
            <div className="ticker text-muted-foreground mb-3">Répartition</div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}/>
                <Tooltip formatter={(v)=>FMT(v)}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="lg:col-span-2 hard-border divide-y">
            {debts.map((d) => (
              <div key={d.id} className="p-4 hover:bg-secondary/30 flex items-center justify-between" data-testid={`debt-${d.id}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <div className={`w-2 h-12 ${d.priority === "rouge" ? "bg-[#D90429]" : d.priority === "orange" ? "bg-[#E9C46A]" : "bg-[#2A9D8F]"}`}/>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold truncate">{d.creditor}</span>
                      <span className="ticker text-muted-foreground">{d.account}</span>
                      {d.status === "en_retard" && <span className="ticker bg-[#D90429] text-white px-2 py-0.5">EN RETARD</span>}
                      {d.status === "entente" && <span className="ticker bg-[#2A9D8F] text-white px-2 py-0.5">entente</span>}
                    </div>
                    <div className="ticker text-muted-foreground mt-0.5">
                      {d.interest_rate}% · min {FMT(d.min_payment)}{d.due_day ? ` · dû le ${d.due_day}` : ""}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="font-mono text-lg font-semibold">{FMT(d.balance)}</div>
                  <button onClick={()=>del(d.id)} className="text-muted-foreground hover:text-[#D90429]" data-testid={`debt-del-${d.id}`}><Trash size={16}/></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {debts.length === 0 && <div className="hard-border p-8 text-sm text-muted-foreground">Aucune dette enregistrée. Ajoute-les pour activer les alertes et stratégies IA.</div>}
    </div>
  );
}

function BillsPanel({ bills, reload }) {
  const empty = { name: "", amount: "", account: "perso", category: "autre", due_day: "1", recurrence: "mensuel", auto_pay: false, notes: "" };
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/bills", { ...form, amount: parseFloat(form.amount), due_day: parseInt(form.due_day) });
      setForm(empty); setOpen(false); reload();
      toast.success("Facture enregistrée");
    } catch { toast.error("Erreur"); }
  };

  const del = async (id) => { await api.delete(`/finance/bills/${id}`); reload(); };

  const today = new Date().getDate();
  const sorted = [...bills].sort((a, b) => a.due_day - b.due_day);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display font-bold text-2xl">{FMT(bills.filter(b=>b.recurrence==="mensuel").reduce((s,b)=>s+b.amount,0))}</div>
          <div className="ticker text-muted-foreground">Factures mensuelles</div>
        </div>
        <button data-testid="bill-add-btn" onClick={()=>setOpen(!open)} className="bg-[#D90429] hover:bg-[#BF0424] text-white px-5 py-2.5 ticker flex items-center gap-2 transition-colors">
          <Plus size={14}/> {open ? "Annuler" : "Ajouter"}
        </button>
      </div>

      {open && (
        <form onSubmit={add} className="hard-border p-5 grid grid-cols-1 md:grid-cols-4 gap-3 bg-secondary/30" data-testid="bill-form">
          <input data-testid="b-name" required placeholder="Nom (ex: Hypothèque, Telus)" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="hard-border px-3 py-2 md:col-span-2"/>
          <input data-testid="b-amount" required type="number" step="0.01" placeholder="Montant $" value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})} className="hard-border px-3 py-2 font-mono"/>
          <input data-testid="b-due" required type="number" min="1" max="31" placeholder="Jour dû" value={form.due_day} onChange={(e)=>setForm({...form, due_day:e.target.value})} className="hard-border px-3 py-2"/>
          <select data-testid="b-account" value={form.account} onChange={(e)=>setForm({...form, account:e.target.value})} className="hard-border px-3 py-2 bg-white">
            <option value="perso">Perso</option><option value="compagnie">Compagnie</option><option value="fondation">Fondation</option>
          </select>
          <select data-testid="b-recurrence" value={form.recurrence} onChange={(e)=>setForm({...form, recurrence:e.target.value})} className="hard-border px-3 py-2 bg-white">
            <option value="mensuel">Mensuel</option><option value="bimensuel">Bimensuel</option>
            <option value="trimestriel">Trimestriel</option><option value="annuel">Annuel</option><option value="ponctuel">Ponctuel</option>
          </select>
          <input data-testid="b-cat" placeholder="Catégorie" value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} className="hard-border px-3 py-2"/>
          <label className="hard-border px-3 py-2 flex items-center gap-2 bg-white cursor-pointer">
            <input type="checkbox" checked={form.auto_pay} onChange={(e)=>setForm({...form, auto_pay:e.target.checked})}/>
            <span className="text-sm">Paiement auto</span>
          </label>
          <button data-testid="b-submit" className="bg-foreground hover:bg-[#D90429] text-white py-2.5 ticker md:col-span-4 transition-colors">Enregistrer la facture</button>
        </form>
      )}

      <div className="hard-border divide-y" data-testid="bills-list">
        {sorted.length === 0 && <div className="p-6 text-sm text-muted-foreground">Aucune facture. Ajoute-les pour activer le calendrier.</div>}
        {sorted.map((b) => {
          const daysUntil = b.due_day >= today ? b.due_day - today : (30 - today + b.due_day);
          return (
            <div key={b.id} className="p-4 flex items-center justify-between hover:bg-secondary/30">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 hard-border flex flex-col items-center justify-center bg-white">
                  <div className="font-mono text-lg font-semibold leading-none">{b.due_day}</div>
                  <div className="ticker text-[9px] text-muted-foreground">jour</div>
                </div>
                <div>
                  <div className="font-semibold flex items-center gap-2">
                    {b.name}
                    {b.auto_pay && <span className="ticker bg-[#2A9D8F] text-white px-1.5 py-0.5">AUTO</span>}
                  </div>
                  <div className="ticker text-muted-foreground">{b.account} · {b.recurrence} · {b.category}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="font-mono font-semibold">{FMT(b.amount)}</div>
                  <div className="ticker text-muted-foreground">dans {daysUntil}j</div>
                </div>
                <button onClick={()=>del(b.id)} className="text-muted-foreground hover:text-[#D90429]" data-testid={`bill-del-${b.id}`}><Trash size={16}/></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GoalsPanel({ goals, reload }) {
  const empty = { title: "", target_amount: "", current_amount: "0", deadline: "", category: "dette" };
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/goals", {
        ...form,
        target_amount: parseFloat(form.target_amount),
        current_amount: parseFloat(form.current_amount) || 0,
        deadline: form.deadline || null,
      });
      setForm(empty); setOpen(false); reload();
      toast.success("Objectif créé");
    } catch { toast.error("Erreur"); }
  };

  const updateAmount = async (id, newAmount) => {
    await api.patch(`/finance/goals/${id}`, { current_amount: parseFloat(newAmount) || 0 });
    reload();
  };

  const del = async (id) => { await api.delete(`/finance/goals/${id}`); reload(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="font-display font-bold text-2xl">{goals.length} objectifs</div>
        <button data-testid="goal-add-btn" onClick={()=>setOpen(!open)} className="bg-[#D90429] hover:bg-[#BF0424] text-white px-5 py-2.5 ticker flex items-center gap-2 transition-colors"><Plus size={14}/> {open?"Annuler":"Nouvel objectif"}</button>
      </div>

      {open && (
        <form onSubmit={add} className="hard-border p-5 grid grid-cols-1 md:grid-cols-4 gap-3 bg-secondary/30" data-testid="goal-form">
          <input data-testid="g-title" required placeholder="Titre (ex: Régler Visa Infinite)" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} className="hard-border px-3 py-2 md:col-span-2"/>
          <select data-testid="g-cat" value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} className="hard-border px-3 py-2 bg-white">
            <option value="dette">Dette</option><option value="epargne">Épargne</option>
            <option value="investissement">Investissement</option><option value="autre">Autre</option>
          </select>
          <input data-testid="g-deadline" type="date" value={form.deadline} onChange={(e)=>setForm({...form, deadline:e.target.value})} className="hard-border px-3 py-2"/>
          <input data-testid="g-target" required type="number" step="0.01" placeholder="Cible $" value={form.target_amount} onChange={(e)=>setForm({...form, target_amount:e.target.value})} className="hard-border px-3 py-2 font-mono"/>
          <input data-testid="g-current" type="number" step="0.01" placeholder="Actuel $" value={form.current_amount} onChange={(e)=>setForm({...form, current_amount:e.target.value})} className="hard-border px-3 py-2 font-mono"/>
          <button data-testid="g-submit" className="bg-foreground hover:bg-[#D90429] text-white py-2.5 ticker md:col-span-2 transition-colors">Créer l'objectif</button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 hard-border">
        {goals.length === 0 && <div className="p-8 text-sm text-muted-foreground md:col-span-2">Aucun objectif. Crées-en un pour suivre ta progression.</div>}
        {goals.map((g, i) => {
          const pct = g.target_amount > 0 ? Math.min(100, (g.current_amount / g.target_amount) * 100) : 0;
          return (
            <div key={g.id} className={`p-6 ${i % 2 === 0 ? "md:border-r border-border" : ""} border-b border-border`} data-testid={`goal-${g.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="ticker text-muted-foreground mb-1">{g.category}{g.deadline ? ` · d'ici ${new Date(g.deadline).toLocaleDateString("fr-CA")}` : ""}</div>
                  <div className="font-display font-bold text-lg truncate">{g.title}</div>
                </div>
                <button onClick={()=>del(g.id)} className="text-muted-foreground hover:text-[#D90429]"><Trash size={16}/></button>
              </div>
              <div className="mt-4">
                <div className="flex items-baseline justify-between">
                  <div className="font-mono text-sm">{FMT(g.current_amount)} / {FMT(g.target_amount)}</div>
                  <div className="font-mono text-sm font-semibold">{pct.toFixed(0)}%</div>
                </div>
                <div className="mt-2 h-2 bg-secondary">
                  <div className="h-full bg-[#D90429] transition-all" style={{ width: `${pct}%` }}/>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input type="number" step="0.01" placeholder="Nouveau montant" className="flex-1 hard-border px-3 py-1.5 text-sm font-mono"
                  onBlur={(e) => e.target.value && updateAmount(g.id, e.target.value)} data-testid={`goal-update-${g.id}`}/>
                <span className="ticker text-muted-foreground">Entrée pour MAJ</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TransactionsPanel({ txs, reload }) {
  const [form, setForm] = useState({ account: "perso", type: "revenu", category: "", amount: "", description: "" });
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/finance/transactions", { ...form, amount: parseFloat(form.amount) });
      setForm({ account: "perso", type: "revenu", category: "", amount: "", description: "" });
      reload(); toast.success("Transaction ajoutée");
    } catch { toast.error("Erreur"); }
  };
  const del = async (id) => { await api.delete(`/finance/transactions/${id}`); reload(); };

  return (
    <div className="space-y-6">
      <form onSubmit={add} className="hard-border p-5 grid grid-cols-1 md:grid-cols-6 gap-3 bg-secondary/30" data-testid="tx-form">
        <select data-testid="tx-account" value={form.account} onChange={(e)=>setForm({...form, account:e.target.value})} className="hard-border px-3 py-2 bg-white">
          <option value="perso">Perso</option><option value="compagnie">Compagnie</option><option value="fondation">Fondation</option>
        </select>
        <select data-testid="tx-type" value={form.type} onChange={(e)=>setForm({...form, type:e.target.value})} className="hard-border px-3 py-2 bg-white">
          <option value="revenu">Revenu</option><option value="depense">Dépense</option><option value="dette">Dette</option>
        </select>
        <input data-testid="tx-category" required placeholder="Catégorie" value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} className="hard-border px-3 py-2"/>
        <input data-testid="tx-amount" required type="number" step="0.01" placeholder="Montant $" value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})} className="hard-border px-3 py-2 font-mono"/>
        <input data-testid="tx-desc" placeholder="Description" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} className="hard-border px-3 py-2 md:col-span-2"/>
        <button data-testid="tx-submit" className="bg-[#D90429] text-white md:col-span-6 py-2.5 ticker hover:bg-[#BF0424] flex items-center justify-center gap-2 transition-colors"><Plus size={14}/> Ajouter</button>
      </form>

      <div className="hard-border divide-y" data-testid="tx-list">
        {txs.length === 0 && <div className="p-6 text-sm text-muted-foreground">Aucune transaction.</div>}
        {txs.map((t) => (
          <div key={t.id} className="px-5 py-3 flex items-center justify-between hover:bg-secondary/30">
            <div className="flex items-center gap-4 min-w-0">
              <span className={`ticker px-2 py-0.5 ${t.type==="revenu"?"bg-[#2A9D8F] text-white":t.type==="depense"?"bg-[#D90429] text-white":"bg-[#2B2D42] text-white"}`}>{t.type}</span>
              <span className="ticker text-muted-foreground">{t.account}</span>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{t.category}</div>
                <div className="text-xs text-muted-foreground truncate">{t.description || "—"}</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="font-mono font-semibold">{FMT(t.amount)}</div>
              <button onClick={()=>del(t.id)} className="text-muted-foreground hover:text-[#D90429]" data-testid={`tx-del-${t.id}`}><Trash size={16}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImportPanel({ reload }) {
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [account, setAccount] = useState("perso");
  const [defaultType, setDefaultType] = useState("depense");
  const [busy, setBusy] = useState(false);

  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return { headers: [], rows: [] };
    const splitLine = (l) => l.split(",").map(s => s.trim().replace(/^"|"$/g, ""));
    const hdrs = splitLine(lines[0]).map(h => h.toLowerCase());
    const data = lines.slice(1).map(l => {
      const vals = splitLine(l);
      const obj = {};
      hdrs.forEach((h, i) => obj[h] = vals[i] || "");
      return obj;
    });
    return { headers: hdrs, rows: data };
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const { headers, rows } = parseCSV(reader.result);
      setHeaders(headers); setRows(rows);
      toast.success(`${rows.length} lignes détectées`);
    };
    reader.readAsText(file);
  };

  const doImport = async () => {
    if (rows.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/finance/import-csv", { account, default_type: defaultType, rows });
      toast.success(`${data.created} transactions créées · ${data.skipped} ignorées`);
      setRows([]); setHeaders([]);
      reload();
    } catch (e) { toast.error("Erreur import"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="hard-border p-6">
        <div className="ticker text-muted-foreground mb-4">Import CSV — 1. Choisis le fichier</div>
        <input type="file" accept=".csv,text/csv" onChange={onFile} className="hard-border px-3 py-2 bg-white" data-testid="csv-file"/>
        <div className="text-xs text-muted-foreground mt-3">Colonnes attendues (ou synonymes): <code>date, description, amount, category</code>. Montants négatifs = dépenses.</div>
      </div>
      {rows.length > 0 && (
        <div className="hard-border p-6 space-y-4" data-testid="csv-preview">
          <div className="ticker text-muted-foreground">Aperçu — 2. Configure l'import</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select value={account} onChange={(e)=>setAccount(e.target.value)} className="hard-border px-3 py-2 bg-white" data-testid="csv-account">
              <option value="perso">Compte perso</option><option value="compagnie">Compte compagnie</option><option value="fondation">Compte fondation</option>
            </select>
            <select value={defaultType} onChange={(e)=>setDefaultType(e.target.value)} className="hard-border px-3 py-2 bg-white" data-testid="csv-default-type">
              <option value="depense">Détecter rev/dép selon signe</option>
              <option value="revenu">Tout comme revenu</option>
            </select>
            <button onClick={doImport} disabled={busy} className="bg-[#D90429] hover:bg-[#BF0424] text-white py-2.5 ticker flex items-center justify-center gap-2 transition-colors disabled:opacity-60" data-testid="csv-import-btn">
              <Upload size={14}/> {busy ? "Import…" : `Importer ${rows.length} lignes`}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-mono">
              <thead className="bg-secondary/40">
                <tr>{headers.map(h => <th key={h} className="text-left px-3 py-2 ticker">{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.slice(0, 10).map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    {headers.map(h => <td key={h} className="px-3 py-1.5 text-xs">{r[h]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 10 && <div className="ticker text-muted-foreground p-2">+{rows.length - 10} autres lignes…</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function StrategyPanel() {
  const [form, setForm] = useState({ objectif: "Rembourser mes dettes prioritaires", situation: "", approche: "auto" });
  const [advice, setAdvice] = useState("");
  const [busy, setBusy] = useState(false);

  const ask = async () => {
    setBusy(true); setAdvice("");
    try {
      const { data } = await api.post("/finance/strategy", form);
      setAdvice(data.advice);
    } catch (e) { toast.error("Erreur IA"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="hard-border p-5 bg-secondary/30">
        <div className="font-semibold text-sm flex items-center gap-2"><Sparkle size={16} className="text-[#D90429]"/> Conseil stratégique IA</div>
        <div className="text-xs text-muted-foreground mt-1">L'IA utilise ton profil, tes dettes, tes factures et ton contexte pour te donner un plan d'attaque précis.</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="ticker text-muted-foreground block mb-2">Objectif</label>
          <input data-testid="strategy-objectif" value={form.objectif} onChange={(e)=>setForm({...form, objectif:e.target.value})} className="w-full hard-border px-3 py-2.5"/>
        </div>
        <div>
          <label className="ticker text-muted-foreground block mb-2">Approche</label>
          <select data-testid="strategy-approche" value={form.approche} onChange={(e)=>setForm({...form, approche:e.target.value})} className="w-full hard-border px-3 py-2.5 bg-white">
            <option value="auto">Laisser l'IA choisir</option>
            <option value="avalanche">Avalanche (taux + élevés d'abord)</option>
            <option value="boule_de_neige">Boule de neige (petits soldes d'abord)</option>
            <option value="hybride">Hybride</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="ticker text-muted-foreground block mb-2">Contexte additionnel (optionnel)</label>
          <textarea data-testid="strategy-situation" rows={3} value={form.situation} onChange={(e)=>setForm({...form, situation:e.target.value})} placeholder="Décris ta situation, priorités, contraintes…" className="w-full hard-border px-3 py-2.5 resize-y"/>
        </div>
      </div>
      <button data-testid="strategy-btn" onClick={ask} disabled={busy} className="bg-[#0A0A0A] text-white px-5 py-3 ticker hover:bg-[#D90429] transition-colors disabled:opacity-60 flex items-center gap-2">
        <Sparkle size={14}/> {busy ? "Analyse en cours…" : "Obtenir une stratégie personnalisée"}
      </button>
      {advice && <pre data-testid="strategy-output" className="whitespace-pre-wrap hard-border p-6 text-sm leading-relaxed bg-secondary/20 font-[Satoshi]">{advice}</pre>}
    </div>
  );
}

function KPI({ label, value, bordered, highlight, testId }) {
  return (
    <div className={`p-5 ${bordered ? "md:border-l border-border" : ""} border-b border-border`}>
      <div className="ticker text-muted-foreground mb-2">{label}</div>
      <div className={`font-mono text-xl font-semibold ${highlight ? "text-[#D90429]" : ""}`} data-testid={testId}>{value}</div>
    </div>
  );
}

function TaxesPanel() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ type: "vente", amount: "", mode: "from_before_tax", treatment: "taxable", description: "", category: "services" });
  const [setaside, setSetaside] = useState({ amount: "", note: "" });
  const [settingsForm, setSettingsForm] = useState({ tps_number: "", tvq_number: "", filing_frequency: "trimestrielle", last_remittance_date: "", setaside_account_name: "Enveloppe taxes à remettre" });
  const [editSettings, setEditSettings] = useState(false);

  const reload = () => Promise.all([
    api.get("/finance/tax/entries").then(r => setEntries(r.data)),
    api.get("/finance/tax/summary").then(r => setSummary(r.data)),
    api.get("/finance/tax/settings").then(r => {
      setSettings(r.data);
      setSettingsForm({
        tps_number: r.data.tps_number || "",
        tvq_number: r.data.tvq_number || "",
        filing_frequency: r.data.filing_frequency || "trimestrielle",
        last_remittance_date: r.data.last_remittance_date ? r.data.last_remittance_date.slice(0, 10) : "",
        setaside_account_name: r.data.setaside_account_name || "Enveloppe taxes à remettre",
      });
    }),
  ]);
  useEffect(() => { reload().catch(() => {}); }, []);

  const addEntry = async (e) => {
    e.preventDefault();
    try {
      const body = {
        type: form.type, treatment: form.treatment,
        description: form.description, category: form.category,
      };
      if (form.mode === "from_total") body.total = parseFloat(form.amount);
      else body.amount_before_tax = parseFloat(form.amount);
      await api.post("/finance/tax/entries", body);
      setForm({ ...form, amount: "", description: "" });
      reload(); toast.success("Entrée fiscale ajoutée");
    } catch { toast.error("Erreur"); }
  };

  const delEntry = async (id) => { await api.delete(`/finance/tax/entries/${id}`); reload(); };

  const doSetaside = async (sign) => {
    const val = parseFloat(setaside.amount);
    if (!val || val <= 0) { toast.error("Montant invalide"); return; }
    try {
      await api.post("/finance/tax/setaside", { amount: sign * val, note: setaside.note });
      setSetaside({ amount: "", note: "" });
      reload(); toast.success(sign > 0 ? "Ajouté à l'enveloppe" : "Retiré de l'enveloppe");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const saveSettings = async () => {
    try {
      const body = { ...settingsForm };
      if (!body.last_remittance_date) body.last_remittance_date = null;
      await api.put("/finance/tax/settings", body);
      setEditSettings(false); reload(); toast.success("Paramètres sauvegardés");
    } catch { toast.error("Erreur"); }
  };

  // Quick calculator (client-side mirror of backend)
  const TPS = 0.05, TVQ = 0.09975;
  const computeLive = () => {
    const a = parseFloat(form.amount);
    if (!a) return null;
    if (form.treatment !== "taxable") return { before: a, tps: 0, tvq: 0, total: a };
    if (form.mode === "from_total") {
      const before = a / (1 + TPS + TVQ);
      return { before, tps: before * TPS, tvq: before * TVQ, total: a };
    }
    return { before: a, tps: a * TPS, tvq: a * TVQ, total: a * (1 + TPS + TVQ) };
  };
  const live = computeLive();

  if (!summary) return <div className="text-sm text-muted-foreground">Chargement…</div>;

  const netTotal = summary.net_a_remettre.total;
  const ecart = summary.ecart_setaside;

  return (
    <div className="space-y-8" data-testid="taxes-panel">
      <div className="hard-border p-5 bg-secondary/30 flex items-start gap-3">
        <Calculator size={22} className="text-[#D90429] mt-0.5"/>
        <div className="text-sm">
          <strong>Comptabilité simplifiée Québec.</strong> TPS 5 % + TVQ 9,975 %. Saisis tes ventes et achats : l'app ventile les taxes automatiquement et calcule le net à remettre (TPS/TVQ perçues − TPS/TVQ payées).
          {settings?.tps_number || settings?.tvq_number ? <span className="ml-2 text-xs">· N° TPS: {settings.tps_number || "—"} · N° TVQ: {settings.tvq_number || "—"}</span> : null}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 hard-border">
        <KPI label="Ventes (HT)" value={FMT(summary.ventes.before_tax)} testId="tax-ventes"/>
        <KPI label="TPS perçue" value={FMT(summary.ventes.tps)} bordered testId="tax-tps-c"/>
        <KPI label="TVQ perçue" value={FMT(summary.ventes.tvq)} bordered testId="tax-tvq-c"/>
        <KPI label="Net à remettre" value={FMT(netTotal)} bordered highlight testId="tax-net"/>
        <KPI label="Achats (HT)" value={FMT(summary.achats.before_tax)} testId="tax-achats"/>
        <KPI label="TPS payée" value={FMT(summary.achats.tps)} bordered testId="tax-tps-p"/>
        <KPI label="TVQ payée" value={FMT(summary.achats.tvq)} bordered testId="tax-tvq-p"/>
        <KPI label="Enveloppe taxes" value={FMT(summary.setaside_balance)} bordered highlight={ecart < 0} testId="tax-setaside"/>
      </div>

      {ecart < 0 && (
        <div className="hard-border p-4 bg-[#FFF4F4] flex items-center gap-3" data-testid="tax-alert">
          <Warning size={20} weight="fill" className="text-[#D90429]"/>
          <div className="text-sm"><strong>Manque {FMT(Math.abs(ecart))} dans ton enveloppe.</strong> Transfère ce montant pour être prêt à remettre TPS/TVQ.</div>
        </div>
      )}

      {/* Quick add */}
      <div className="hard-border">
        <div className="hard-border-b px-6 py-4 bg-secondary/40 ticker">Nouvelle entrée fiscale</div>
        <form onSubmit={addEntry} className="p-5 grid grid-cols-1 md:grid-cols-6 gap-3">
          <select data-testid="tx-tax-type" value={form.type} onChange={(e)=>setForm({...form, type:e.target.value})} className="hard-border px-3 py-2 bg-white">
            <option value="vente">Vente</option><option value="achat">Achat</option>
          </select>
          <select data-testid="tx-tax-treatment" value={form.treatment} onChange={(e)=>setForm({...form, treatment:e.target.value})} className="hard-border px-3 py-2 bg-white">
            <option value="taxable">Taxable</option><option value="exonere">Exonéré</option><option value="zero">Taxe zéro</option>
          </select>
          <select data-testid="tx-tax-mode" value={form.mode} onChange={(e)=>setForm({...form, mode:e.target.value})} className="hard-border px-3 py-2 bg-white">
            <option value="from_before_tax">Montant avant taxes</option>
            <option value="from_total">Montant total (taxes incluses)</option>
          </select>
          <input data-testid="tx-tax-amount" required type="number" step="0.01" placeholder="Montant $" value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})} className="hard-border px-3 py-2 font-mono"/>
          <input data-testid="tx-tax-cat" placeholder="Catégorie" value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} className="hard-border px-3 py-2"/>
          <input data-testid="tx-tax-desc" placeholder="Description" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} className="hard-border px-3 py-2"/>
          {live && (
            <div className="md:col-span-6 hard-border p-3 bg-secondary/20 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm font-mono" data-testid="tax-live-preview">
              <div><span className="ticker text-muted-foreground">Avant taxes</span><br/>{FMT(live.before)}</div>
              <div><span className="ticker text-muted-foreground">TPS 5%</span><br/>{FMT(live.tps)}</div>
              <div><span className="ticker text-muted-foreground">TVQ 9,975%</span><br/>{FMT(live.tvq)}</div>
              <div className="font-semibold"><span className="ticker text-muted-foreground">Total</span><br/>{FMT(live.total)}</div>
            </div>
          )}
          <button data-testid="tx-tax-submit" className="bg-[#D90429] hover:bg-[#BF0424] text-white md:col-span-6 py-2.5 ticker flex items-center justify-center gap-2 transition-colors"><Plus size={14}/> Enregistrer l'entrée</button>
        </form>
      </div>

      {/* Setaside envelope */}
      <div className="hard-border">
        <div className="hard-border-b px-6 py-4 bg-secondary/40 flex items-center gap-2"><Vault size={16} className="text-[#D90429]"/><span className="ticker">{settings?.setaside_account_name || "Enveloppe taxes"}</span></div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <div className="ticker text-muted-foreground">Solde actuel</div>
            <div className="font-mono text-2xl font-semibold mt-1">{FMT(summary.setaside_balance)}</div>
          </div>
          <input data-testid="setaside-amount" type="number" step="0.01" placeholder="Montant $" value={setaside.amount} onChange={(e)=>setSetaside({...setaside, amount:e.target.value})} className="hard-border px-3 py-2 font-mono"/>
          <input data-testid="setaside-note" placeholder="Note (optionnel)" value={setaside.note} onChange={(e)=>setSetaside({...setaside, note:e.target.value})} className="hard-border px-3 py-2"/>
          <div className="md:col-span-3 flex gap-3">
            <button onClick={()=>doSetaside(1)} data-testid="setaside-add" className="flex-1 bg-[#2A9D8F] hover:opacity-90 text-white py-2 ticker transition-colors">+ Ajouter</button>
            <button onClick={()=>doSetaside(-1)} data-testid="setaside-remove" className="flex-1 hard-border py-2 ticker hover:bg-[#D90429] hover:text-white hover:border-[#D90429] transition-colors">− Retirer (remise)</button>
          </div>
        </div>
      </div>

      {/* Entries list */}
      <div className="hard-border" data-testid="tax-entries-list">
        <div className="hard-border-b px-6 py-4 bg-secondary/40 ticker">Entrées fiscales ({entries.length})</div>
        <div className="divide-y">
          {entries.length === 0 && <div className="p-6 text-sm text-muted-foreground">Aucune entrée fiscale.</div>}
          {entries.map((e) => (
            <div key={e.id} className="px-5 py-3 flex items-center justify-between hover:bg-secondary/30">
              <div className="flex items-center gap-4 min-w-0">
                <span className={`ticker px-2 py-0.5 ${e.type === "vente" ? "bg-[#2A9D8F] text-white" : "bg-[#2B2D42] text-white"}`}>{e.type}</span>
                <span className="ticker text-muted-foreground">{e.treatment}</span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{e.description || e.category}</div>
                  <div className="ticker text-muted-foreground">HT {FMT(e.amount_before_tax)} · TPS {FMT(e.tps)} · TVQ {FMT(e.tvq)}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="font-mono font-semibold">{FMT(e.total)}</div>
                <button onClick={()=>delEntry(e.id)} className="text-muted-foreground hover:text-[#D90429]" data-testid={`tax-del-${e.id}`}><Trash size={16}/></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Settings */}
      <div className="hard-border">
        <div className="hard-border-b px-6 py-4 bg-secondary/40 flex items-center justify-between">
          <span className="ticker">Paramètres fiscaux</span>
          <button onClick={()=>setEditSettings(!editSettings)} className="ticker hover:text-[#D90429]" data-testid="tax-settings-edit">{editSettings ? "Annuler" : "Modifier"}</button>
        </div>
        {editSettings ? (
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
            <input data-testid="tax-tps-num" placeholder="N° d'inscription TPS" value={settingsForm.tps_number} onChange={(e)=>setSettingsForm({...settingsForm, tps_number:e.target.value})} className="hard-border px-3 py-2 font-mono"/>
            <input data-testid="tax-tvq-num" placeholder="N° d'inscription TVQ" value={settingsForm.tvq_number} onChange={(e)=>setSettingsForm({...settingsForm, tvq_number:e.target.value})} className="hard-border px-3 py-2 font-mono"/>
            <select data-testid="tax-freq" value={settingsForm.filing_frequency} onChange={(e)=>setSettingsForm({...settingsForm, filing_frequency:e.target.value})} className="hard-border px-3 py-2 bg-white">
              <option value="mensuelle">Déclaration mensuelle</option>
              <option value="trimestrielle">Déclaration trimestrielle</option>
              <option value="annuelle">Déclaration annuelle</option>
            </select>
            <input data-testid="tax-last-remit" type="date" value={settingsForm.last_remittance_date} onChange={(e)=>setSettingsForm({...settingsForm, last_remittance_date:e.target.value})} className="hard-border px-3 py-2"/>
            <input data-testid="tax-envelope-name" placeholder="Nom de l'enveloppe" value={settingsForm.setaside_account_name} onChange={(e)=>setSettingsForm({...settingsForm, setaside_account_name:e.target.value})} className="hard-border px-3 py-2 md:col-span-2"/>
            <button onClick={saveSettings} data-testid="tax-settings-save" className="bg-foreground hover:bg-[#D90429] text-white py-2.5 ticker md:col-span-2 transition-colors">Enregistrer les paramètres</button>
          </div>
        ) : (
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div><span className="ticker text-muted-foreground">N° TPS</span><div className="font-mono mt-1">{settings?.tps_number || "—"}</div></div>
            <div><span className="ticker text-muted-foreground">N° TVQ</span><div className="font-mono mt-1">{settings?.tvq_number || "—"}</div></div>
            <div><span className="ticker text-muted-foreground">Fréquence</span><div className="mt-1">{settings?.filing_frequency || "—"}</div></div>
            <div><span className="ticker text-muted-foreground">Dernière remise</span><div className="mt-1">{settings?.last_remittance_date ? new Date(settings.last_remittance_date).toLocaleDateString("fr-CA") : "—"}</div></div>
          </div>
        )}
      </div>

      <div className="hard-border p-5 bg-secondary/20">
        <div className="font-semibold text-sm">Intégrations externes — Phase 2d</div>
        <div className="text-sm text-muted-foreground mt-1">Les connexions QuickBooks et WellyBox arrivent en phase suivante. Elles permettront de synchroniser ventes/achats automatiquement et récupérer les reçus sans saisie manuelle.</div>
      </div>
    </div>
  );
}
